/**
 * @module app.shared
 */

import { Container, TaskQueue } from 'aurelia-framework';
export { debounce } from 'app/shared/lib/decorators/debounce';

// T.method(...args: any[]): R
type AnyArgsMethodDescriptor<T, R> = TypedPropertyDescriptor<(this: T, ...args: any[]) => R>;

// T.method(...args: any[]): void
type VoidAnyArgsMethodDescriptor<T> = TypedPropertyDescriptor<(this: T, ...args: any[]) => void>;

// T.method(): void
type VoidNoArgsMethodDescriptor<T> = TypedPropertyDescriptor<(this: T) => void>;

// factory: (decorator arguments) => descriptor
type MethodDecoratorFactory<T, D> = (target: T, methodName: string, descriptor: D) => D;

// various state properties used by various decorators
interface IFunctionState {
    executed?: boolean;
    executing?: boolean;
    suspended?: boolean;
    result?: any;
    args?: any[];
}

function getTaskQueue(): TaskQueue {
    // get task queue out of container
    return Container.instance.get(TaskQueue);
}

function generateDecoratorStateKey(decorator: string, methodName: string): string {
    // generate unique property name to store decorator state
    return `__decorator_${decorator}_${methodName}__`;
}

function getDecoratorState<T extends {}>(target: T, key: string): IFunctionState {
    // see if state has already been stored on object
    if(target.hasOwnProperty(key)) {
        return (<any>target)[key] as IFunctionState;
    }
    // if not, create a new hidden, read-only property with the state
    const result = {};
    Object.defineProperty(target, key, {
        configurable: false,
        enumerable: false,
        writable: false,
        value: result
    });
    return result;
}

export function log<T extends {}, R>(target: T, methodName: string, descriptor: AnyArgsMethodDescriptor<T, R>): AnyArgsMethodDescriptor<T, R> {
    const method = descriptor.value;
    if(method) {
        const logDecorated = function(this: T, ...args: any[]) {
            // generate scope name
            const scopeName = `${target.constructor.name}.${methodName}`;
            // begin logging group (collapsed)
            console.groupCollapsed(scopeName);
            try {
                // log arguments
                console.log({ args: args, this: this, function: scopeName });
                // call original function
                const result = method.apply(this, args);
                // if a result was returned, log result
                if(result !== undefined) {
                    console.log({ return: result, this: this, function: scopeName });
                }
                // return result
                return result;
            }
            catch(error) {
                // log error
                console.error({ catch: error, this: this, function: scopeName });
                // rethrow error
                throw error;
            }
            finally {
                // end logging group
                console.groupEnd();
            }
        }
        descriptor.value = logDecorated;
    }
    return descriptor;
}

export function oneTime<T, R>(target: T, methodName: string, descriptor: AnyArgsMethodDescriptor<T, R>): AnyArgsMethodDescriptor<T, R> {
    const method = descriptor.value;
    const key = generateDecoratorStateKey('oneTime', methodName);
    if(method) {
        const oneTimeDecorated = function(this: T, ...args: any[]) {
            // get method state
            const state = getDecoratorState(this, key);
            // if already executed, return stored result
            if(state.executed) { return state.result; }
            // call original method and store result
            state.result = method.apply(this, args);
            // track executed state
            state.executed = true;
            // return result
            return state.result;
        }
        descriptor.value = oneTimeDecorated;
    }
    return descriptor;
}

export function defer<T>(target: T, methodName: string, descriptor: VoidNoArgsMethodDescriptor<T>): VoidNoArgsMethodDescriptor<T> {
    const method = descriptor.value;
    const key = generateDecoratorStateKey('defer', methodName);
    if(method) {
        const deferDecorated = function(this: T, ...args: any[]) {
            // get method state
            const state = getDecoratorState(this, key);
            // if currently executing, return
            if(state.executing) { return; }
            // track execution state
            state.executing = true;
            // queue execution as microtask
            const taskQueue = getTaskQueue();
            taskQueue.queueMicroTask(() => {
                try {
                    // call original method
                    method.apply(this, args);
                }
                finally {
                    // clear state
                    state.executing = false;
                }
            });
        }
        descriptor.value = deferDecorated;
    }
    return descriptor;
}

export function task<T>(target: T, methodName: string, descriptor: VoidAnyArgsMethodDescriptor<T>): VoidAnyArgsMethodDescriptor<T> {
    const method = descriptor.value;
    if(method) {
        const taskDecorated = function(this: T, ...args: any[]) {
            // get task queue
            const taskQueue = getTaskQueue();
            // queue method as task
            taskQueue.queueTask(() => {
                method.apply(this, args);
            });
        }
        descriptor.value = taskDecorated;
    }
    return descriptor;
}

export function microTask<T>(target: T, methodName: string, descriptor: VoidAnyArgsMethodDescriptor<T>): VoidAnyArgsMethodDescriptor<T> {
    const method = descriptor.value;
    if(method) {
        const microTaskDecorated = function(this: T, ...args: any[]) {
            // get task queue
            const taskQueue = getTaskQueue();
            // queue method as micro task
            taskQueue.queueMicroTask(() => {
                method.apply(this, args);
            });
        }
        descriptor.value = microTaskDecorated;
    }
    return descriptor;
}
